import axios from "axios";
import { getMsalIdToken } from './helpers';
import mitt from "mitt";
let userAccessRetries = 0;
export const emitter = mitt(); // 🔥 global event bus

let isErrorVisible = false;

function showError(error) {
    if (isErrorVisible) return;

    isErrorVisible = true;

    emitter.emit("api-error", error);

    setTimeout(() => {
        isErrorVisible = false;
    }, 3000);
}

const instance = axios.create();

instance.defaults.headers.common["Accept"] = "application/json";
instance.defaults.headers.common["Content-Type"] = "application/json";
instance.defaults.withCredentials = false;

instance.interceptors.response.use(
    async function (response) {
        if (response.status === 401) {
            const idToken = await getMsalIdToken();
            instance.defaults.headers.common["Authorization"] = `Bearer ${idToken}`;
            response.config.headers.Authorization = `Bearer ${idToken}`;
            return instance(response.config); // ✅ retry → re-enters interceptor on every 401
        }
        if (response.status !== 200) {
            const data = response?.data || {};

            const message =
                data.message ||
                data.error ||
                "Something went wrong. Please try again.";

            const errorPayload = {
                defaultMessage: "Something went wrong. Please try again.",
                message,
                error: data.error || '',
                status: data.status || response.status || '',
                requestId: data.requestId || ''
            };

            showError(errorPayload);

            return Promise.resolve({
                error: true,
                status: response.status,
                message,
                data
            });
        }

        return response;
    },

    async function (error) {
        const originalRequest = error.config;

        // ✅ 401 retry
        if (error.response?.status === 401) {
            try {
                if (originalRequest?.url?.includes('retrieveUserAccess')) {
                    userAccessRetries++;
                    if (userAccessRetries >= 1) {
                        userAccessRetries = 0;
                        const { default: router } = await import('@/router');
                        const productId = sessionStorage.getItem("currentProductId") || '';
                        sessionStorage.setItem("unauthorizedAccess", "true"); // ✅ add this
                        router.push({ path: '/unauthorized', query: { productId } });
                        return Promise.resolve({ error: true });
                    }
                }
                const idToken = await getMsalIdToken();

                instance.defaults.headers.common["Authorization"] = `Bearer ${idToken}`;
                originalRequest.headers.Authorization = `Bearer ${idToken}`;

                return instance(originalRequest);
            } catch (e) {
                showError({
                    message: "Session expired. Please login again.",
                    code: 401
                });

                return Promise.resolve({ error: true });
            }
        }

        const data = error?.response?.data || {};

        // ✅ Fallback logic (IMPORTANT)
        const message =
            data.message ||
            data.error ||
            error.message || // axios/system error
            "Something went wrong. Please try again.";

        const errorPayload = {
            defaultMessage: "Something went wrong. Please try again.",
            message,
            error: data.error || '',
            status: data.status || error.response?.status || '',
            requestId: data.requestId || ''
        };

        showError(errorPayload);

        return Promise.resolve({
            error: true,
            status: error.response?.status,
            message,
            data: data
        });
    }
);

export default instance;
